import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronRight, Building2, Users, Store, AlertTriangle, Search, Save } from 'lucide-react';
import { toast } from 'sonner';

// Admin-only Distribution Tree view: Master → Sub → LCO with sub counts.
// Orphans (nodes with unresolved parent references) are surfaced separately.

export default function HierarchyTree() {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [expandedMasters, setExpandedMasters] = useState({});
  const [expandedSubs, setExpandedSubs] = useState({});

  const fetchTree = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/hierarchy/tree`);
      setData(r.data);
      // Expand all Masters by default so ops sees the tree immediately.
      const nextExpanded = {};
      (r.data.masters || []).forEach((m) => { nextExpanded[m.customer_id] = true; });
      setExpandedMasters(nextExpanded);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load hierarchy');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTree(); /* eslint-disable-next-line */ }, []);

  const matchesQuery = (node) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (node.company_name || '').toLowerCase().includes(s) ||
      (node.customer_code || '').toLowerCase().includes(s) ||
      (node.customer_id || '').toLowerCase().includes(s)
    );
  };

  const filteredMasters = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.masters;
    return data.masters
      .map((m) => {
        const subs = (m.subs || [])
          .map((s) => {
            const lcos = (s.lcos || []).filter(matchesQuery);
            const subMatches = matchesQuery(s) || lcos.length > 0;
            return subMatches ? { ...s, lcos } : null;
          })
          .filter(Boolean);
        const directLcos = (m.direct_lcos || []).filter(matchesQuery);
        const anyChild = subs.length > 0 || directLcos.length > 0;
        const selfMatch = matchesQuery(m);
        return selfMatch || anyChild ? { ...m, subs, direct_lcos: directLcos } : null;
      })
      .filter(Boolean);
  }, [data, q]);

  const filteredOrphans = useMemo(() => {
    if (!data) return [];
    return (data.orphans || []).filter(matchesQuery);
  // eslint-disable-next-line
  }, [data, q]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="hierarchy-tree-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Distribution Tree</h1>
          <p className="text-sm text-slate-500">Master Distributor → Sub Distributor → LCO. Numbers show downstream counts and active IPTV subscriptions.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / code"
              className="pl-8 w-72"
              data-testid="hierarchy-search"
            />
          </div>
          <Button variant="outline" onClick={fetchTree} data-testid="hierarchy-refresh">Refresh</Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatChip label="Master Distributors" value={data.totals.masters} icon={<Building2 className="h-4 w-4" />} />
          <StatChip label="Sub Distributors"    value={data.totals.subs}    icon={<Users className="h-4 w-4" />} />
          <StatChip label="LCOs"                value={data.totals.lcos}    icon={<Store className="h-4 w-4" />} />
          <StatChip label="Orphans"             value={data.totals.orphans} icon={<AlertTriangle className="h-4 w-4" />} highlight={data.totals.orphans > 0} />
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Loading tree…</p>}

      {!loading && data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Tree</CardTitle>
              <CardDescription>Click a Master to expand its Sub Distributors, then a Sub to expand its LCOs.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {filteredMasters.length === 0 && (
                <p className="text-sm text-slate-500">No Master Distributor matches this search.</p>
              )}
              {filteredMasters.map((m) => (
                <div key={m.customer_id} className="rounded-md border" data-testid={`master-node-${m.customer_id}`}>
                  <button
                    onClick={() =>
                      setExpandedMasters((prev) => ({ ...prev, [m.customer_id]: !prev[m.customer_id] }))
                    }
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left"
                  >
                    <div className="flex items-center gap-2">
                      {expandedMasters[m.customer_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <Building2 className="h-4 w-4 text-indigo-600" />
                      <span className="font-medium">{m.company_name}</span>
                      <span className="text-xs text-slate-400 font-mono">{m.customer_code}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline">Master</Badge>
                      <Badge variant="secondary">{m.sub_count_downstream} sub{m.sub_count_downstream === 1 ? '' : 's'}</Badge>
                      <Badge variant="secondary">{m.direct_lco_count} direct LCO{m.direct_lco_count === 1 ? '' : 's'}</Badge>
                      <Badge className="bg-emerald-100 text-emerald-800">{m.sub_count} IPTV subs</Badge>
                    </div>
                  </button>

                  {expandedMasters[m.customer_id] && (
                    <div className="border-t pl-6 py-2 space-y-2 bg-slate-50/40">
                      {(m.subs || []).map((s) => (
                        <div key={s.customer_id} className="rounded border bg-white" data-testid={`sub-node-${s.customer_id}`}>
                          <button
                            onClick={() =>
                              setExpandedSubs((prev) => ({ ...prev, [s.customer_id]: !prev[s.customer_id] }))
                            }
                            className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left"
                          >
                            <div className="flex items-center gap-2">
                              {expandedSubs[s.customer_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              <Users className="h-4 w-4 text-sky-600" />
                              <span className="font-medium text-sm">{s.company_name}</span>
                              <span className="text-xs text-slate-400 font-mono">{s.customer_code}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <Badge variant="outline">Sub</Badge>
                              <Badge variant="secondary">{s.lco_count} LCO{s.lco_count === 1 ? '' : 's'}</Badge>
                              <Badge className="bg-emerald-100 text-emerald-800">{s.sub_count} IPTV subs</Badge>
                            </div>
                          </button>
                          {expandedSubs[s.customer_id] && s.lcos.length > 0 && (
                            <ul className="border-t divide-y">
                              {s.lcos.map((l) => (
                                <li key={l.customer_id} className="px-3 py-1.5 flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    <Store className="h-3.5 w-3.5 text-amber-600" />
                                    <span>{l.company_name}</span>
                                    <span className="text-xs text-slate-400 font-mono">{l.customer_code}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs">
                                    <Badge variant="outline">LCO</Badge>
                                    <Badge className="bg-emerald-50 text-emerald-700">{l.sub_count} subs</Badge>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                      {(m.direct_lcos || []).length > 0 && (
                        <div className="rounded border bg-white">
                          <div className="px-3 py-1.5 text-xs font-medium text-slate-500">
                            Direct LCOs (no Sub Distributor)
                          </div>
                          <ul className="border-t divide-y">
                            {m.direct_lcos.map((l) => (
                              <li key={l.customer_id} className="px-3 py-1.5 flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <Store className="h-3.5 w-3.5 text-amber-600" />
                                  <span>{l.company_name}</span>
                                  <span className="text-xs text-slate-400 font-mono">{l.customer_code}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <Badge variant="outline">LCO</Badge>
                                  <Badge className="bg-emerald-50 text-emerald-700">{l.sub_count} subs</Badge>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(m.subs || []).length === 0 && (m.direct_lcos || []).length === 0 && (
                        <p className="text-xs text-slate-500 italic">No downstream customers.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {filteredOrphans.length > 0 && (
            <Card className="border-amber-300">
              <CardHeader>
                <CardTitle className="text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Orphans ({filteredOrphans.length})
                </CardTitle>
                <CardDescription>Customers whose parent references don't resolve — pick a parent inline and save.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="divide-y">
                  {filteredOrphans.slice(0, 100).map((o) => (
                    <OrphanRow
                      key={o.customer_id}
                      orphan={o}
                      masters={data.masters}
                      API={API}
                      onReassigned={fetchTree}
                    />
                  ))}
                  {filteredOrphans.length > 100 && (
                    <li className="italic text-xs text-slate-500 py-1">…and {filteredOrphans.length - 100} more</li>
                  )}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function StatChip({ label, value, icon, highlight }) {
  return (
    <Card className={highlight ? 'border-amber-300' : ''}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className={`text-2xl font-bold ${highlight ? 'text-amber-700' : ''}`}>{value}</p>
        </div>
        <div className={`p-2 rounded-md ${highlight ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{icon}</div>
      </CardContent>
    </Card>
  );
}

// Inline parent-picker for a single orphan row. For an orphan LCO: pick a
// Master Distributor and (optionally) a Sub Distributor under that Master.
// For an orphan Sub Distributor: just pick a Master.
function OrphanRow({ orphan, masters, API, onReassigned }) {
  const [partnerId, setPartnerId] = useState('');
  const [distId, setDistId] = useState('');
  const [saving, setSaving] = useState(false);

  const chosenMaster = useMemo(
    () => masters.find((m) => m.customer_id === partnerId) || null,
    [masters, partnerId]
  );
  const subOptions = chosenMaster?.subs || [];

  const canSave = partnerId && !saving;

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/customers/${orphan.customer_id}/parent`, {
        parent_partner_id: partnerId,
        parent_distributor_id: orphan.customer_type === 'lco' ? (distId || null) : null,
      });
      toast.success(`Reassigned ${orphan.company_name || orphan.customer_code}`);
      onReassigned && onReassigned();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reassignment failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm"
        data-testid={`orphan-row-${orphan.customer_id}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="truncate">{orphan.company_name || '—'}</span>
        <span className="text-xs text-slate-400 font-mono truncate">{orphan.customer_code || orphan.customer_id}</span>
        <Badge variant="outline" className="capitalize whitespace-nowrap">{orphan.customer_type?.replace('_', ' ')}</Badge>
      </div>
      <div className="flex items-center gap-2">
        <Select value={partnerId} onValueChange={(v) => { setPartnerId(v); setDistId(''); }}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid={`orphan-master-${orphan.customer_id}`}>
            <SelectValue placeholder="Master Distributor…" />
          </SelectTrigger>
          <SelectContent>
            {masters.map((m) => (
              <SelectItem key={m.customer_id} value={m.customer_id}>
                {m.company_name || m.customer_code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {orphan.customer_type === 'lco' && subOptions.length > 0 && (
          <Select value={distId} onValueChange={setDistId}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid={`orphan-sub-${orphan.customer_id}`}>
              <SelectValue placeholder="Sub Distributor (optional)" />
            </SelectTrigger>
            <SelectContent>
              {subOptions.map((s) => (
                <SelectItem key={s.customer_id} value={s.customer_id}>
                  {s.company_name || s.customer_code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          size="sm"
          className="h-8"
          disabled={!canSave}
          onClick={save}
          data-testid={`orphan-save-${orphan.customer_id}`}
        >
          <Save className="h-3.5 w-3.5 mr-1" />
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </li>
  );
}
